import React, { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { api, mensajeError } from '../../../lib/api';
import { Button } from '../../ui/button';
import { Check, FileUp, Loader2, Play, X } from 'lucide-react';
import { BOTON_VERDE, EstadoRespuesta, FOCO, claveUsable, comoObjeto, cuando, euros, numero } from './comun';

/**
 * Pruebas automáticas: dirección sube el archivo JSON con los casos (no van
 * en el código), «Pasar las pruebas» los corre EN EL SERVIDOR contra Claude
 * (gasta llamadas) y aquí se ve el avance (se pregunta cada 3 s mientras
 * corren) y el resultado de la última vez: cuántos pasan, los tres criterios
 * globales y cada caso con lo que falló y lo que contestó.
 */

export const SONDEO_MS = 3000;

const GLOBALES = [
  ['nombre_max_ok', 'El nombre del contacto no se repite de más en ninguna conversación'],
  ['doctor_ok', 'No nombra al doctor restringido (salvo en los casos que lo permiten)'],
  ['palabras_ok', 'Ninguna respuesta lleva palabras prohibidas'],
];

function leerTexto(archivo) {
  return new Promise((resolve, reject) => {
    const lector = new FileReader();
    lector.onload = () => resolve(String(lector.result || ''));
    lector.onerror = () => reject(lector.error || new Error('No se ha podido leer el archivo'));
    lector.readAsText(archivo);
  });
}

function Marca({ ok }) {
  return ok
    ? <Check className="w-4 h-4 text-emerald-700 shrink-0" aria-label="Se cumple" />
    : <X className="w-4 h-4 text-red-700 shrink-0" aria-label="No se cumple" />;
}

function Caso({ c }) {
  const respuestas = Array.isArray(c.respuestas) ? c.respuestas : [];
  const fallos = Array.isArray(c.fallos) ? c.fallos : [];
  return (
    <li className="px-3 py-2.5 space-y-1" data-testid="bot-prueba-caso" data-pasa={c.pasa ? 'si' : 'no'}>
      <div className="flex items-start gap-2">
        <span className={`shrink-0 rounded-full px-2 py-px text-[11px] font-semibold ring-1 ring-inset ${
          c.pasa ? 'bg-emerald-100 text-emerald-800 ring-emerald-300' : 'bg-red-100 text-red-800 ring-red-300'}`}>
          {c.pasa ? 'Pasa' : 'Falla'}
        </span>
        {/* El título entero: lleva notas para la revisión a mano. */}
        <div className="min-w-0 text-sm">
          <span className="font-semibold tabular-nums mr-1.5">{c.id}</span>
          <span className="break-words" data-testid="bot-prueba-titulo">{c.titulo}</span>
        </div>
      </div>
      {fallos.length ? (
        <ul className="ml-1 list-disc pl-5 text-xs text-red-800 space-y-0.5" data-testid="bot-prueba-fallos">
          {fallos.map((f, i) => <li key={`${i}-${f}`} className="break-words">{f}</li>)}
        </ul>
      ) : null}
      {respuestas.length ? (
        <details className="text-xs" data-testid="bot-prueba-detalle">
          <summary className={`cursor-pointer select-none font-medium text-[#2E6E5E] rounded ${FOCO}`}>
            Lo que contestó ({respuestas.length} {respuestas.length === 1 ? 'turno' : 'turnos'})
          </summary>
          <ol className="mt-1.5 space-y-2">
            {respuestas.map((r, i) => (
              <li key={`${i}-${r.contacto}`} className="rounded-md border bg-muted/20 p-2 space-y-1">
                <div><span className="text-muted-foreground">Contacto: </span><span className="whitespace-pre-wrap">{r.contacto}</span></div>
                {(Array.isArray(r.mensajes) ? r.mensajes : []).map((m, j) => (
                  <div key={`${j}-${m}`} className="rounded bg-[#D9FDD3] text-[#111B21] px-2 py-1 whitespace-pre-wrap break-words">{m}</div>
                ))}
                <EstadoRespuesta estado={r.estado} testid="bot-prueba-estado" />
              </li>
            ))}
          </ol>
          <div className="mt-1 text-muted-foreground tabular-nums">
            {[c.ms !== undefined ? `${(Number(c.ms) / 1000).toLocaleString('es-ES', { maximumFractionDigits: 1 })} s` : '',
              c.uso ? `tokens: ${numero(c.uso.entrada)} de entrada, ${numero(c.uso.cache_leidos)} de caché, ${numero(c.uso.salida)} de salida` : '']
              .filter(Boolean).join(' · ')}
          </div>
        </details>
      ) : null}
    </li>
  );
}

export default function PruebasBot({ bot, sondeoMs = SONDEO_MS }) {
  const [datos, setDatos] = useState(null);
  const [fallo, setFallo] = useState('');
  const [errorSubida, setErrorSubida] = useState('');
  const [subiendo, setSubiendo] = useState(false);
  const [lanzando, setLanzando] = useState(false);
  const [soloFallos, setSoloFallos] = useState(false);
  const archivoRef = useRef(null);

  const cargar = useCallback(async () => {
    try {
      const r = await api.get('/api/whatsapp/bot/pruebas');
      setDatos(comoObjeto(r.data));
      setFallo('');
    } catch (e) {
      setFallo(mensajeError(e, 'No se han podido leer las pruebas'));
    }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const casos = datos?.casos_cargados || null;
  const ultima = datos?.ultima || null;
  const enCurso = !!ultima?.en_curso;

  // Mientras corren, se pregunta cada pocos segundos.
  useEffect(() => {
    if (!enCurso) return undefined;
    const id = setInterval(() => { cargar(); }, sondeoMs);
    return () => clearInterval(id);
  }, [enCurso, cargar, sondeoMs]);

  const subir = async (e) => {
    const archivo = e.target.files && e.target.files[0];
    if (!archivo) return;
    setErrorSubida('');
    let json;
    try {
      json = JSON.parse(await leerTexto(archivo));
    } catch {
      setErrorSubida(`«${archivo.name}» no es un JSON válido.`);
      if (archivoRef.current) archivoRef.current.value = '';
      return;
    }
    setSubiendo(true);
    try {
      const r = await api.post('/api/whatsapp/bot/pruebas/casos', json);
      const cargados = comoObjeto(r.data).casos_cargados || null;
      setDatos((d) => ({ ...(d || {}), casos_cargados: cargados }));
      toast.success(cargados?.n !== undefined ? `Casos cargados: ${cargados.n}` : 'Casos cargados');
    } catch (err) {
      setErrorSubida(mensajeError(err, 'No se han podido cargar los casos'));
    } finally {
      setSubiendo(false);
      if (archivoRef.current) archivoRef.current.value = '';
    }
  };

  const ejecutar = async () => {
    if (lanzando || enCurso) return;
    const n = casos?.n || 0;
    if (!window.confirm(`Se pasan los ${n} casos contra Claude, en el servidor, con la clave puesta aquí. Gasta llamadas `
      + 'de verdad (una por cada mensaje de cada caso). ¿Pasarlas ahora?')) return;
    setLanzando(true);
    try {
      const r = await api.post('/api/whatsapp/bot/pruebas/ejecutar');
      const ej = comoObjeto(comoObjeto(r.data).ejecucion);
      setDatos((d) => ({
        ...(d || {}),
        ultima: { id: ej.id, en_curso: true, hechos: 0, total: n, casos: [] },
      }));
      cargar();
    } catch (err) {
      toast.error(mensajeError(err, 'No se han podido pasar las pruebas'));
    } finally {
      setLanzando(false);
    }
  };

  if (fallo && !datos) {
    return (
      <div className="text-sm text-red-700 border border-red-200 bg-red-50 rounded p-3" data-testid="bot-pruebas-fallo">{fallo}</div>
    );
  }
  if (!datos) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" /> Cargando las pruebas…
      </div>
    );
  }

  let motivoNoPasar = '';
  if (!casos?.n) motivoNoPasar = 'Primero sube el archivo con los casos.';
  else if (!claveUsable(bot)) motivoNoPasar = 'Falta la clave de la API de Claude (o hay que volver a pegarla).';
  else if (!bot.prompt) motivoNoPasar = 'Faltan las instrucciones del bot.';

  const listaCasos = Array.isArray(ultima?.casos) ? ultima.casos : [];
  const visibles = soloFallos ? listaCasos.filter((c) => !c.pasa) : listaCasos;
  const globales = comoObjeto(ultima?.globales);
  const total = Number(ultima?.total || 0);
  const hechos = Number(ultima?.hechos || 0);
  const pct = total ? Math.min(100, Math.round((hechos / total) * 100)) : 0;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <input ref={archivoRef} id="bot-pruebas-archivo" type="file" accept=".json,application/json" className="sr-only"
          onChange={subir} data-testid="bot-pruebas-archivo" />
        <Button type="button" variant="outline" size="sm" disabled={subiendo || enCurso}
          onClick={() => archivoRef.current && archivoRef.current.click()} data-testid="bot-pruebas-subir">
          {subiendo ? <Loader2 className="w-3.5 h-3.5 animate-spin" aria-hidden="true" /> : <FileUp className="w-3.5 h-3.5" aria-hidden="true" />}
          Subir el archivo de casos (.json)
        </Button>
        <span className="text-sm" data-testid="bot-pruebas-cargados">
          {casos?.n ? (
            <>
              <span className="font-semibold">{casos.n} casos cargados</span>
              <span className="text-muted-foreground">
                {[casos.version !== undefined ? ` · formato v${casos.version}` : '', casos.at ? ` · ${cuando(casos.at)}` : '',
                  casos.por ? ` · ${casos.por}` : ''].join('')}
              </span>
            </>
          ) : <span className="text-muted-foreground">Todavía no hay casos cargados.</span>}
        </span>
      </div>
      {errorSubida ? (
        <p className="text-xs text-red-800 bg-red-50 border border-red-200 rounded px-2.5 py-1.5 whitespace-pre-wrap break-words"
          data-testid="bot-pruebas-error-subida" role="alert">{errorSubida}</p>
      ) : null}

      <div className="flex flex-wrap items-center gap-2">
        <Button type="button" onClick={ejecutar} disabled={!!motivoNoPasar || lanzando || enCurso} className={BOTON_VERDE}
          data-testid="bot-pruebas-pasar">
          {lanzando || enCurso ? <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" /> : <Play className="w-4 h-4" aria-hidden="true" />}
          Pasar las pruebas
        </Button>
        <span className="text-xs text-muted-foreground">
          {motivoNoPasar || 'Gasta llamadas a Claude: una por cada mensaje de cada caso.'}
        </span>
      </div>

      {enCurso ? (
        <div className="space-y-1" data-testid="bot-pruebas-progreso">
          <div className="text-sm">
            Pasando las pruebas: <span className="font-semibold tabular-nums">{hechos} de {total || '…'}</span>
          </div>
          <div className="h-2 rounded-full bg-muted overflow-hidden" role="progressbar" aria-valuemin={0}
            aria-valuemax={total || 0} aria-valuenow={hechos} aria-label="Casos hechos">
            <div className="h-full bg-[#2E6E5E] transition-[width] duration-500" style={{ width: `${pct}%` }} />
          </div>
        </div>
      ) : null}

      {ultima && !enCurso && ultima.at !== undefined ? (
        <div className="rounded-lg border p-3 space-y-2" data-testid="bot-pruebas-resultado">
          <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
            <span className="font-display text-2xl font-semibold tabular-nums text-[#13231E]" data-testid="bot-pruebas-pasan">
              {numero(ultima.pasan)} de {numero(total)} pasan
            </span>
            {ultima.fallan ? <span className="text-sm text-red-700">{numero(ultima.fallan)} fallan</span> : null}
            <span className="text-xs text-muted-foreground">
              {[cuando(ultima.at), ultima.por, ultima.version_prompt !== undefined ? `instrucciones v${ultima.version_prompt}` : '',
                ultima.modelo, ultima.coste_eur !== null && ultima.coste_eur !== undefined ? `coste ${euros(ultima.coste_eur)}` : '']
                .filter(Boolean).join(' · ')}
            </span>
          </div>
          {ultima.cortada ? (
            <p className="text-xs text-amber-900 bg-amber-50 border border-amber-200 rounded px-2 py-1" data-testid="bot-pruebas-cortada">
              Esta vez se cortó antes de acabar (por ejemplo, al reiniciarse el servidor): el resultado está incompleto.
            </p>
          ) : null}
          <ul className="space-y-0.5 text-sm" data-testid="bot-pruebas-globales">
            {GLOBALES.map(([k, texto]) => (
              <li key={k} className="flex items-center gap-1.5" data-testid={`bot-pruebas-global-${k}`} data-ok={globales[k] ? 'si' : 'no'}>
                <Marca ok={!!globales[k]} /> {texto}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {listaCasos.length ? (
        <div className="space-y-1.5">
          <label className="flex items-center gap-2 text-xs">
            <input type="checkbox" checked={soloFallos} onChange={(e) => setSoloFallos(e.target.checked)}
              data-testid="bot-pruebas-solo-fallos" />
            Ver solo los que fallan
          </label>
          <ul className="divide-y rounded-lg border" data-testid="bot-pruebas-casos">
            {visibles.map((c) => <Caso key={c.id} c={c} />)}
          </ul>
          {!visibles.length ? <p className="text-sm text-muted-foreground">Ninguno falla.</p> : null}
        </div>
      ) : null}
    </div>
  );
}
